import * as React from "react";
import { Link } from "react-router-dom";
import { Nav, Button } from "react-bootstrap";
import {
  Account, AppConfiguration, PrivateRoutes, useAppSelector, useGetDownloadFolderIdQuery, FSEmptyLinkedCollectionID, DriveEntryType, buildNavigationLink, PreveilDownloads, useOpenInFileManagerMutation,
  Message, DriveErrorMessages, useAppDispatch, convertB64toUUID
} from "src/common";
import { Icon } from "src/components";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  current_account?: Account;
  active_path?: string;
};

const routes = [
  PrivateRoutes.shared_route,
  PrivateRoutes.invitations_route,
  PrivateRoutes.trash_route,
  PrivateRoutes.watched_route,
  PrivateRoutes.starred_route
];

const is_web = AppConfiguration.buildForWeb();

function DriveNavigationComponent(props: AllProps) {
  const { active_path, current_account } = props;
  const dispatch = useAppDispatch();
  const root_info = useAppSelector((state: RootState) => state.drive.root_info);
  const path = useAppSelector((state: RootState) => state.drive.current_directory_path);
  const file_manager = useAppSelector((state: RootState) => state.app.os_info?.file_manager);
  const current_directory = useAppSelector((state: RootState) => state.drive.current_directory);
  const current_collection = useAppSelector((state: RootState) => state.drive.current_collection);
  const [openLocally] = useOpenInFileManagerMutation();
  const { data } = useGetDownloadFolderIdQuery({ user_id: current_account?.user_id || "" }, { skip: is_web || (!current_account && !!root_info) });

  // Description: Opens the folder through filesync.
  async function openFolder() {
    if (!current_collection) return;
    const url = !!current_directory?.collection_id && !!current_directory?.id && !!path ? buildNavigationLink(current_directory.collection_id, current_directory.id, DriveEntryType.DIR) : "";
    const is_pseudo_root = !!current_collection && !!current_collection.disjointed_acl_node && current_collection.id === current_directory?.id;
    !!current_account &&
      await openLocally({
        user_id: current_account.user_id,
        path,
        url,
        is_cloud_drive: true,
        collUUID: !!current_directory?.collection_id ? convertB64toUUID(current_directory.collection_id) : "",
        dirUUID: !!current_directory?.id && !is_pseudo_root ? convertB64toUUID(current_directory.id) : "",
      }).unwrap().catch((error: unknown) => {
        dispatch(uiActions.handleRequestErrors(new Message(DriveErrorMessages.error_opening_folder_path), error));
      });
  }

  return <>
    {
      !is_web &&
      <Button className="left-nav-btn" aria-label={`Open in ${file_manager}`}
        disabled={!current_collection}
        onClick={() => openFolder()}>
        <Icon className="pv-icon-document-multiple" />
        <span>Open in {file_manager}</span>
      </Button>
    }
    <Nav>
      <Link aria-label="My PreVeil Drive"
        className={`nav-link${!!active_path && !routes.includes(active_path) ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.drive_root}`}>
        <Icon className="ficon-folder" />
        <span>My PreVeil</span>
      </Link>
      <Link aria-label="Starred Drive"
        className={`nav-link${active_path === PrivateRoutes.starred_route ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.starred_route}`}>
        <Icon className="ficon-star" />
        <span>Starred</span>
      </Link>
      <Link aria-label="Shared Drive"
        className={`nav-link${active_path === PrivateRoutes.shared_route ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.shared_route}`}>
        <Icon className="ficon-share-2" />
        <span>Shared</span>
      </Link>
      <Link aria-label="Invitations Drive"
        className={`nav-link${active_path === PrivateRoutes.invitations_route ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.invitations_route}`}>
        <Icon className="ficon-send" />
        <span>Invitations</span>
      </Link>
      <Link aria-label="Watched Drive"
        className={`nav-link${active_path === PrivateRoutes.watched_route ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.watched_route}`} >
        <Icon className="ficon-eye" />
        <span>Watched</span>
      </Link>
      <Link aria-label="Trash Drive"
        className={`nav-link${active_path === PrivateRoutes.trash_route ? " active" : ""}`}
        to={`/${PrivateRoutes.drive_route}/${PrivateRoutes.trash_route}`} >
        <Icon className="ficon-trash-2" />
        <span>Trash</span>
      </Link>
      {
        (!is_web && !!root_info && !!data && !!data.id && data.id !== FSEmptyLinkedCollectionID) &&
        <Link aria-label={PreveilDownloads}
          className="nav-link"
          to={buildNavigationLink(root_info.collection_id, data.id, DriveEntryType.DIR)} >
          <Icon className="ficon-download" />
          <span>{PreveilDownloads}</span>
        </Link>
      }
    </Nav >
  </>;
}

export default React.memo(DriveNavigationComponent);
